/**
 * Probability slider edit component
 */

import React, { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { clampProbability, getProbabilityColor } from "../utils/probability-utils";
import type { ProbabilitySliderEditProps } from "../types";

const PROBABILITY_STEP = 1;

export function ProbabilitySliderEdit({
  editValue,
  setEditValue,
  onSave,
  onCancel,
  rowId,
  fieldId,
  keyDownHandler,
  blurSaveHandler,
  EDIT_SLIDER_CLASS,
  EDIT_INPUT_NUMBER_CLASS,
}: ProbabilitySliderEditProps) {
  const [isEditingNumber, setIsEditingNumber] = useState(false);
  const num = clampProbability(
    editValue === "" || editValue === undefined ? 0 : Number(editValue) || 0
  );

  // Enforce 0–100 on mount
  useEffect(() => {
    const n = Number(editValue);
    if (editValue !== "" && !Number.isNaN(n) && (n < 0 || n > 100)) {
      setEditValue(String(clampProbability(n)));
    }
  }, []);

  const apply = useCallback((value: number) => setEditValue(String(clampProbability(value))), [setEditValue]);

  const handleNumberBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value === "" ? 0 : Number(e.target.value);
      apply(Number.isNaN(v) ? num : v);
      setIsEditingNumber(false);
      blurSaveHandler(e);
    },
    [apply, num, blurSaveHandler]
  );

  const handleNumberKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        const v = (e.target as HTMLInputElement).value === "" ? 0 : Number((e.target as HTMLInputElement).value);
        apply(Number.isNaN(v) ? num : v);
        setIsEditingNumber(false);
      }
      keyDownHandler(e);
    },
    [apply, num, keyDownHandler]
  );

  return (
    <div className="flex items-center gap-1.5 min-w-[180px]">
      <button
        type="button"
        aria-label="Decrease probability"
        onClick={() => apply(num - PROBABILITY_STEP)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-input bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground text-sm font-medium select-none"
      >
        −
      </button>
      <input
        type="range"
        min={0}
        max={100}
        value={num}
        onChange={(e) => setEditValue(String(clampProbability(Number(e.target.value))))}
        className={`${EDIT_SLIDER_CLASS} probability-slider flex-1 min-w-0`}
        style={{ "--probability-thumb-color": getProbabilityColor(num) } as React.CSSProperties}
        autoFocus
      />
      <button
        type="button"
        aria-label="Increase probability"
        onClick={() => apply(num + PROBABILITY_STEP)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-input bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground text-sm font-medium select-none"
      >
        +
      </button>
      {isEditingNumber ? (
        <Input
          type="number"
          min={0}
          max={100}
          value={editValue}
          onChange={(e) => {
            const v = e.target.value === "" ? "" : Number(e.target.value);
            if (v === "") {
              setEditValue("");
              return;
            }
            setEditValue(String(clampProbability(Number.isNaN(v) ? num : v)));
          }}
          className={`${EDIT_INPUT_NUMBER_CLASS} w-14 shrink-0`}
          autoFocus
          onBlur={handleNumberBlur}
          onKeyDown={handleNumberKeyDown}
        />
      ) : (
        <button
          type="button"
          onClick={() => setIsEditingNumber(true)}
          className="text-sm font-medium tabular-nums w-10 shrink-0 text-left hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded px-1"
        >
          {num}%
        </button>
      )}
    </div>
  );
}
